// Per-route SEO metadata. Titles ≤ 60 chars, descriptions 50-160 chars.
// Avoid prose dashes; UK English spelling.
import { INDUSTRIES } from "@/components/roi/industryConfig";
export type RouteMeta = { title: string; description: string; keywords?: string[] };

export const SITE_URL = "https://astreonaigroup.com";
export const DEFAULT_META: RouteMeta = {
  title: "Astreon AI Group | The AI consultancy that builds",
  description:
    "UK AI consultancy that designs, builds and integrates agents, automation and dashboards for measurable business outcomes.",
};

export const ROUTE_META: Record<string, RouteMeta> = {
  "/": DEFAULT_META,

  "/product": {
    title: "Proprietary AI products | Astreon AI Group",
    description:
      "Production ready AI products built from our consulting engagements, including voice, workflow, insights and engagement systems.",
  },
  "/products/voice": {
    title: "Voice AI assistants | Astreon AI Group",
    description:
      "Natural sounding voice AI that answers calls, books appointments and routes enquiries for service businesses, 24 hours a day.",
  },
  "/products/flow": {
    title: "Workflow automation | Astreon AI Group",
    description:
      "Astreon Flow automates back office processes end to end, connecting your tools, agents and data into reliable, auditable pipelines.",
  },
  "/products/insights": {
    title: "AI analytics dashboards | Astreon AI Group",
    description:
      "Astreon Insights turns operational data into live dashboards and forecasts, so leaders see performance and risk in real time.",
  },
  "/products/engage": {
    title: "Customer engagement AI | Astreon AI Group",
    description:
      "Astreon Engage personalises outreach across email, chat and voice, lifting conversion with timely, on brand customer messages.",
  },
  "/products/custom": {
    title: "Custom AI builds | Astreon AI Group",
    description:
      "Bespoke AI systems engineered around your data, workflows and compliance needs, delivered by our consulting and engineering team.",
  },

  "/security": {
    title: "Security practices | Astreon AI Group",
    description:
      "How Astreon AI Group protects client data: encryption, access controls, monitoring and the policies behind our AI deployments.",
  },
  "/trust-center": {
    title: "Trust centre | Astreon AI Group",
    description:
      "Compliance, privacy and security posture for Astreon AI Group, including subprocessors, certifications and data handling.",
  },

  "/case-studies": {
    title: "Case studies | Astreon AI Group",
    description:
      "Editorial case studies from UK businesses using Astreon AI Group to deploy agents, automation and analytics at scale.",
  },
  "/case-scenarios": {
    title: "Case scenarios | Astreon AI Group",
    description:
      "Illustrative scenarios showing how practical AI and automation could improve operations across UK businesses.",
  },
  "/case-scenarios/construction-supplier-query-response": {
    title: "A supplier query takes three days because nobody owns it.",
    description: "Supplier queries took three days because nobody owned them from start to finish. Here is what tracing twelve of them showed and how response time fell 85%.",
    keywords: ["construction supply chain", "construction project delays UK", "construction procurement efficiency", "supplier query management", "workflow automation UK", "operational efficiency UK"],
  },
  "/case-scenarios/lettings-agency-enquiry-response": {
    title: "The first agent to reply usually wins the viewing.",
    description:
      "A lettings agency lost applicants to whoever replied first, as evening and weekend enquiries waited until morning. Here is how reply time fell to 3 minutes.",
    keywords: [
      "property lead management",
      "estate agent conversion",
      "property lead qualification",
      "lettings agency enquiry response",
      "workflow automation UK",
      "estate agent productivity UK",
    ],
  },
  "/case-scenarios/boutique-hotel-direct-bookings": {
    title: "Guests wanted to book direct. Nobody replied in time.",
    description:
      "A 28 room boutique hotel lost direct bookings to platforms because enquiries waited hours for a reply. Here is how response time fell from 5 hours to 2 minutes.",
    keywords: [
      "hotel revenue management",
      "hospitality operations UK",
      "guest journey management",
      "personalised guest experience",
      "out of hours hotel guest communication",
      "hotel direct bookings",
    ],
  },
  "/case-scenarios/candidate-communication-automation": {
    title: "Candidates were not leaving over the role. They left over silence.",
    description:
      "The majority of candidate withdrawals cited slow or inconsistent communication, not dissatisfaction with the role or the client. Here is what changed when consultants stopped managing follow up manually.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "business efficiency UK",
      "candidate communication automation",
      "recruitment agency efficiency",
    ],
  },
  "/case-scenarios/physiotherapy-group-performance-visibility": {
    title: "Leadership thought they knew which site was struggling.",
    description:
      "A physiotherapy group believed its newest site was underperforming. Once the numbers were normalised properly, the opposite was true.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "healthcare operational efficiency",
      "multisite performance reporting",
      "private practice management UK",
    ],
  },
  "/case-scenarios/enterprise-financial-services-onboarding": {
    title: "Six hours of work took six weeks. Nobody had asked why.",
    description:
      "A financial services onboarding process involved under six hours of actual work spread across six weeks. See where the time went and what changed.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "business efficiency UK",
      "automation consultancy UK",
      "financial services automation",
    ],
  },
  "/case-scenarios/ecommerce-returns-processing": {
    title: "An 8 day wait for a refund that needed no decision.",
    description:
      "Customers waited 8 days for refund decisions that needed no judgement. Here is how returns processing time fell 73% and negative reviews about returns fell 79%.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "business efficiency UK",
      "ecommerce returns management",
      "returns processing automation",
      "ecommerce customer experience",
    ],
  },
  "/case-studies/dental-practice-no-show-recovery": {
    title: "A practice was losing £6,200 a month without knowing it. Here is what changed.",
    description:
      "The practice manager guessed six or seven unfilled appointments a week. The actual number was eighteen. Here is what we built and what four months of data showed.",
    keywords: [
      "private practice management UK",
      "patient no-show rate healthcare",
      "dental practice efficiency",
      "private clinic management UK",
      "patient appointment reminders UK",
      "practice scheduling optimisation",
    ],
  },
  "/testimonials": {
    title: "Client testimonials | Astreon AI Group",
    description:
      "What leaders across healthcare, professional services and retail say about working with Astreon AI Group.",
  },

  "/careers": {
    title: "Careers | Astreon AI Group",
    description:
      "Join Astreon AI Group and help leading UK organisations deploy practical, production grade AI across their operations.",
  },
  "/careers/apply": {
    title: "Apply to join Astreon AI Group",
    description:
      "Submit your application to join the Astreon AI Group consulting and engineering team and work on real AI deployments.",
  },

  "/about": {
    title: "About Astreon AI Group",
    description:
      "Astreon AI Group is a UK AI consultancy that advises, designs and ships production AI for ambitious organisations.",
  },
  "/about/who-we-are": {
    title: "Who we are | Astreon AI Group",
    description:
      "The story, values and operating model behind Astreon AI Group, an independent UK consultancy focused on practical AI.",
  },
  "/about/leadership": {
    title: "Leadership | Astreon AI Group",
    description:
      "Meet the founders and leadership team guiding Astreon AI Group's consulting, engineering and product practice.",
  },
  "/about/purpose": {
    title: "Our purpose | Astreon AI Group",
    description:
      "Why Astreon AI Group exists: to make AI deliver real, measurable value for the businesses that adopt it.",
  },
  "/about/history": {
    title: "Our history | Astreon AI Group",
    description:
      "A short history of Astreon AI Group, from advisory roots to a full stack AI consulting and engineering firm.",
  },
  "/about/aspiration": {
    title: "Our aspiration | Astreon AI Group",
    description:
      "Where Astreon AI Group is heading: building the AI infrastructure that powers modern, intelligent UK enterprises.",
  },
  "/about/governance": {
    title: "Governance | Astreon AI Group",
    description:
      "How Astreon AI Group governs its AI work, including ethics, risk management and accountability frameworks.",
  },
  "/about/team": {
    title: "Our team | Astreon AI Group",
    description:
      "The consultants, engineers and designers shipping AI products and engagements at Astreon AI Group.",
  },

  "/contact-us": {
    title: "Contact Astreon AI Group",
    description:
      "Reach Astreon AI Group to discuss an AI engagement, request pricing or arrange an introductory consultation.",
  },
  "/contact": {
    title: "Contact Astreon AI Group",
    description:
      "Get in touch with Astreon AI Group for AI consulting, build services and product enquiries from across the UK.",
  },
  "/book": {
    title: "Book a call | Astreon AI Group",
    description:
      "Schedule a 30 minute consultation with the Astreon AI Group team to scope your AI project or product roadmap.",
  },
  "/book-a-demo": {
    title: "Book a demo | Astreon AI Group",
    description:
      "Request a live demo of Astreon AI Group products and see how voice, workflow and insights modules fit your business.",
  },
  "/pricing-request": {
    title: "Request pricing | Astreon AI Group",
    description:
      "Tell us about your project and we'll send tailored pricing for Astreon AI Group consulting and build engagements.",
  },

  "/industries/healthcare": {
    title: "AI for healthcare | Astreon AI Group",
    description:
      "AI for clinics, hospitals and private practices: front desk automation, triage agents and operational analytics.",
  },
  "/industries/logistics": {
    title: "AI for logistics | Astreon AI Group",
    description:
      "Routing, forecasting and exception handling AI for UK logistics and supply chain operators.",
  },
  "/industries/real-estate": {
    title: "AI for real estate | Astreon AI Group",
    description:
      "AI agents and automation for estate agencies, lettings and property managers, from lead capture to viewings.",
  },
  "/industries/law-firms": {
    title: "AI for law firms | Astreon AI Group",
    description:
      "Drafting, research and intake AI for UK law firms, designed for accuracy, confidentiality and audit.",
  },
  "/industries/ecommerce": {
    title: "AI for ecommerce | Astreon AI Group",
    description:
      "Personalisation, support and merchandising AI to grow conversion and retention for UK ecommerce brands.",
  },
  "/industries/construction": {
    title: "AI for construction | Astreon AI Group",
    description:
      "Predictive scheduling, safety and project intelligence AI for UK construction and engineering firms.",
  },
  "/industries/finance": {
    title: "AI for finance | Astreon AI Group",
    description:
      "Compliant AI for finance teams and FS firms: document intelligence, risk, reporting and customer agents.",
  },
  "/industries/hospitality": {
    title: "AI for hospitality | Astreon AI Group",
    description:
      "Guest experience, bookings and operations AI for hotels, restaurants and hospitality groups in the UK.",
  },
  "/industries/retail": {
    title: "AI for retail | Astreon AI Group",
    description:
      "Personalisation, inventory and clienteling AI for UK retail brands looking to lift basket size and loyalty.",
  },
  "/industries/education": {
    title: "AI for education | Astreon AI Group",
    description:
      "Personalised learning, admissions and operations AI for schools, universities and training providers.",
  },
  "/industries/manufacturing": {
    title: "AI for manufacturing | Astreon AI Group",
    description:
      "Predictive maintenance, quality and planning AI for UK manufacturers digitising their operations.",
  },
  "/industries/it-enterprises": {
    title: "AI for IT and operational enterprises | Astreon AI Group",
    description:
      "Service desk automation, infrastructure intelligence and access governance AI for IT and operational enterprises.",
  },
  "/industries/insurance": {
    title: "AI for insurance | Astreon AI Group",
    description:
      "Claims, underwriting and customer service AI for UK insurance carriers, brokers and MGAs.",
  },
  "/industries/travel-tourism": {
    title: "AI for travel and tourism | Astreon AI Group",
    description:
      "Itinerary, support and yield AI for UK travel operators, OTAs and tourism boards to grow margin and loyalty.",
  },
  "/industries/recruitment": {
    title: "AI for recruitment | Astreon AI Group",
    description:
      "CV screening, interview scheduling and candidate nurture AI for UK recruitment agencies and talent teams.",
  },

  "/reels": {
    title: "Film library | Astreon AI Group",
    description:
      "Short films from Astreon AI Group on operational problems we fix and the AI systems we build to solve them.",
  },

  "/clinarium": {
    title: "Clinarium | Predictive operations OS for clinics",
    description:
      "Clinarium forecasts which clinic appointments will be missed, fixes them inside your rules, and proves the revenue it recovers.",
  },

  "/voice-agent-builder": {
    title: "Build and call your own AI voice agent | Astreon AI Group",
    description:
      "Design a custom AI voice agent in minutes, choose its voice, tone and call handling, then call it live in your browser.",
  },

  "/roi-calculator": {
    title: "AI ROI calculator | Astreon AI Group",
    description:
      "Calculate the revenue your business recovers each month with an AI front desk OS, with editable assumptions and transparent sources.",
  },

  "/clinic-genius-ai": {
    title: "Clinic Genius AI | Front Desk OS for clinics",
    description:
      "Clinic Genius AI is the front desk operating system for private clinics, automating calls, bookings and follow ups.",
  },

  "/how-we-help/industry-depth": {
    title: "Industry depth | Astreon AI Group",
    description:
      "How Astreon AI Group goes deep in target sectors to deliver AI that fits real workflows, data and regulation.",
  },
  "/how-we-help/advisory-build": {
    title: "Advisory and build | Astreon AI Group",
    description:
      "Strategy, architecture and delivery for AI initiatives, from first workshop to production rollout and support.",
  },
  "/how-we-help/ai-embedded": {
    title: "AI embedded | Astreon AI Group",
    description:
      "Embed senior AI engineers and consultants in your team to ship AI features alongside your existing roadmap.",
  },

  "/privacy-policy": {
    title: "Privacy policy | Astreon AI Group",
    description:
      "How Astreon AI Group collects, uses and protects personal data across our websites, products and engagements.",
  },
  "/terms-of-use": {
    title: "Terms of use | Astreon AI Group",
    description:
      "The terms that govern use of Astreon AI Group's websites, products and online services.",
  },
  "/newsletters": {
    title: "Newsletters | Astreon AI Group",
    description:
      "Practical AI insights for UK leaders, delivered by the Astreon AI Group consulting and engineering team.",
  },
  "/unsubscribe": {
    title: "Unsubscribe | Astreon AI Group",
    description: "Manage your email preferences and unsubscribe from Astreon AI Group communications.",
  },

  "/blog": {
    title: "Blog | Astreon AI Group",
    description:
      "Field notes, playbooks and case studies on applied AI from the Astreon AI Group consulting and engineering team.",
  },
  "/blog/first-ninety-seconds-discovery-call": {
    title: "Your discovery call is decided in the first ninety seconds.",
    description: "Most discovery calls turn into a presentation within two minutes and the buyer disengages. Here is how one opening question changes everything that follows.",
    keywords: ["automation consultancy UK", "private practice management UK", "healthcare reception management", "workflow automation UK", "healthcare operational efficiency", "discovery call questions"],
  },
  "/blog/data-not-as-bad-as-you-think": {
    title: "Your data is not too messy.",
    description:
      "Most practices think their data is too messy for automation. It rarely is. Here is how a narrower first question makes untidy data usable, and where it is not.",
    keywords: [
      "data readiness for automation",
      "business automation UK",
      "workflow automation UK",
      "private practice management UK",
      "healthcare operational efficiency",
      "automation consultancy UK",
    ],
  },
  "/blog/call-at-740pm-after-hours": {
    title: "Somebody Rang Your Practice at 7.40pm and Hung Up.",
    description:
      "Callers who reach voicemail after hours rarely leave a message and ring the next practice instead. Here is what happens to that call and how to catch it.",
    keywords: [
      "out of hours patient enquiry handling",
      "missed calls private practice",
      "patient call handling",
      "healthcare reception management",
      "new patient conversion healthcare",
      "private clinic management UK",
    ],
  },
  "/blog/what-reception-desks-taught-us": {
    title: "The Front Desk Is the Most Informed Place in Your Practice.",
    description:
      "We walked into private practices in Bedford without an appointment and learned who really knows how a practice runs. Here is what the front desk taught us.",
    keywords: [
      "private practice management UK",
      "healthcare reception management",
      "private clinic management UK",
      "healthcare operational efficiency",
      "workflow automation UK",
      "private practices Bedford",
    ],
  },
  "/blog/receptionist-trained-our-system": {
    title: "The receptionist knew which patients needed more care.",
    description:
      "A receptionist's two extra questions on rebooking calls caught patients needing a treatment plan change early. Here is how we built that into every booking.",
    keywords: [
      "private practice management UK",
      "healthcare reception management",
      "private clinic management UK",
      "patient engagement between appointments",
      "personalised patient care",
      "healthcare operational efficiency",
    ],
  },
  "/blog/slack-message-enterprise-pilot": {
    title: "One Slow Reply Nearly Ended a Promising Enterprise Pilot.",
    description:
      "A single unanswered question in an internal meeting nearly paused a promising enterprise pilot. Here is what happened and what we now do differently.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "automation consultancy UK",
      "enterprise automation UK",
    ],
  },
  "/blog/trust-not-understanding": {
    title: "She Trusted Us Completely. She Understood Almost Nothing.",
    description:
      "A client signed off on a technical build without really understanding what she was approving, because she had decided she did not need to if she trusted the people building it. Here is why that made us uncomfortable and what we changed.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "automation consultancy UK",
      "private practice management UK",
      "healthcare operational efficiency",
    ],
  },
  "/blog/15-person-business-enterprise-lesson": {
    title: "A 15-Person Client Taught Us How We Won a Much Larger One",
    description:
      "We spent six weeks with a fifteen-person logistics business building one automation. What its owner told us ended up being the exact insight that won us a much larger enterprise client three months later.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "business efficiency UK",
      "automation consultancy UK",
      "enterprise automation UK",
    ],
  },
  "/blog/younger-brother-family-practice": {
    title: "The younger brother said no. He was right to protect it.",
    description:
      "Two brothers ran a physiotherapy clinic they had built together over fifteen years. The younger one arrived at our first meeting with his arms folded. Here is what he taught us about how family healthcare businesses actually make decisions.",
    keywords: [
      "private practice management UK",
      "healthcare operational efficiency",
      "clinical admin reduction",
      "private clinic management UK",
      "healthcare reception management",
      "private practice patient journey",
    ],
  },
  "/blog/honesty-almost-cost-us-the-deal": {
    title: "The honesty that almost cost us the deal.",
    description:
      "We were in a room with senior stakeholders, watching a plan take shape that we believed would fail. Here is what we said and what happened next.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "automation consultancy UK",
      "digital transformation UK",
      "enterprise automation UK",
    ],
  },
  "/blog/no-shows-fixable-system-problem": {
    title: "Your no-show rate isn't bad luck. It's a fixable system problem.",
    description:
      "No-shows in UK private clinics run at 10 to 18% depending on specialty. Here's why they happen, what the patterns actually tell you, and how the best-run practices bring them under control.",
    keywords: [
      "patient no-show rate healthcare",
      "private practice appointment management",
      "patient appointment reminders UK",
      "patient conversion rate healthcare",
      "private clinic revenue management",
      "healthcare scheduling",
    ],
  },
  "/blog/private-practice-growth-without-headcount": {
    title: "Growing Revenue But Not Margin? More Headcount Isn't the Answer.",
    description:
      "Most private practices respond to growth by adding staff. Here's why that keeps margins flat and what separates the practices growing profitably from the ones just getting bigger.",
    keywords: [
      "private practice management UK",
      "medical practice operations",
      "private clinic management UK",
      "healthcare practice growth",
      "clinical capacity management",
      "practice operational efficiency",
    ],
  },
  "/blog/clinical-documentation-burden": {
    title: "Two hours of paperwork for every hour with a patient. That has to change.",
    description:
      "Clinical documentation consumes more of a clinician's day than most practices track. Here's what the burden actually looks like and what happens when practices start to reduce it.",
    keywords: [
      "clinical admin reduction",
      "healthcare operational efficiency",
      "NHS admin burden",
      "clinical workflow automation",
      "how to reduce admin in GP practice",
      "healthcare documentation tools",
    ],
  },

  "/blog/simple-vs-agentic-automation": {
    title: "Simple AI vs agentic AI explained",
    description:
      "What separates simple AI from agentic AI, where each fits and how UK businesses should choose between them.",
  },
  "/blog/private-practice-management": {
    title: "AI in private healthcare practices",
    description:
      "How UK private clinics use AI to automate the front desk, reduce no shows and free clinicians for patient care.",
  },
  "/blog/small-law-firm-efficiency": {
    title: "AI for small law firms in the UK",
    description:
      "Where small UK law firms get the most value from AI, from intake and drafting to research and matter management.",
  },
  "/blog/estate-agent-productivity": {
    title: "Will AI replace estate agents?",
    description:
      "A grounded look at what AI changes in estate agency work, and what only human agents can still do well.",
  },
  "/blog/retail-personalisation": {
    title: "AI personalisation in UK retail",
    description:
      "How UK retailers use AI personalisation to lift conversion, basket size and loyalty across digital and store.",
  },
  "/blog/personalised-learning-education": {
    title: "AI personalised learning in education",
    description:
      "How schools and universities use AI to tailor learning paths, support teachers and improve student outcomes.",
  },
  "/blog/personalised-guest-experience-hospitality": {
    title: "AI for hospitality guest experiences",
    description:
      "How hotels and hospitality groups use AI to personalise stays, streamline operations and grow direct revenue.",
  },
  "/blog/predictive-construction-delays": {
    title: "Predictive AI for construction delays",
    description:
      "How predictive AI helps construction teams flag delays early, reallocate resource and protect project margins.",
  },
  "/blog/workflow-automation-healthcare-operations": {
    title: "Workflow automation in healthcare ops",
    description:
      "How workflow automation removes manual back office work in healthcare, from referrals to billing and reporting.",
  },
  "/blog/ecommerce-product-discovery": {
    title: "Why online shoppers leave without buying",
    description:
      "The average ecommerce site loses 97 out of 100 visitors. Learn why product discovery is the problem most retailers ignore, and what fixing it actually looks like.",
    keywords: [
      "AI product discovery ecommerce",
      "personalised shopping experience UK",
      "AI recommendation engine ecommerce",
      "ecommerce conversion rate UK",
      "intelligent search ecommerce",
      "AI shopping personalisation",
    ],
  },
  "/blog/personalised-care-precision-medicine": {
    title: "Why personalised care is changing and what that means for healthcare",
    description:
      "Every patient is different, yet most healthcare still treats them the same. Learn how precision medicine and AI driven patient engagement are changing that, and what it means for providers.",
    keywords: [
      "personalised care AI",
      "precision medicine UK",
      "AI patient engagement",
      "remote patient monitoring",
      "AI healthcare UK",
      "AI clinical decision support",
    ],
  },
  "/blog/factory-of-the-future-intelligent-manufacturing": {
    title: "The factory of the future is already running",
    description:
      "The manufacturers running leaner, breaking down less, and shipping more reliably are not planning to adopt AI. They already have. Here's what intelligent operations actually looks like.",
    keywords: [
      "AI manufacturing UK",
      "smart factory automation",
      "predictive maintenance AI",
      "manufacturing operations AI",
      "industrial automation UK",
      "AI quality control manufacturing",
    ],
  },
  "/blog/great-clinicians-drowning-in-admin": {
    title: "Your clinicians got into medicine to help people. So why is admin stopping them?",
    description:
      "Most clinical time disappears into admin that has nothing to do with medicine. Here's what practices look like when that changes, and what better patient care actually delivers.",
    keywords: [
      "AI for GP practices UK",
      "smarter patient care",
      "clinical admin automation",
      "AI patient engagement UK",
      "healthcare AI UK",
      "between-appointment patient care",
    ],
  },
  "/blog/personalised-travel-experiences": {
    title: "Generic travel experiences are losing you repeat bookings.",
    description:
      "In tourism, customers remember the feeling, not the technology. Here's why personalisation at scale has become the defining gap between the brands winning repeat bookings and those losing them.",
    keywords: [
      "AI for travel industry UK",
      "personalised travel experience",
      "AI dynamic pricing travel",
      "travel recommendation engine",
      "AI customer service travel",
      "tourism personalisation",
    ],
  },
  "/blog/insurance-claims-smarter-risk": {
    title: "The claims process is where customer trust is won or lost and where most insurers are losing.",
    description:
      "Insurance customers form their opinion of you at the moment they make a claim. Here's what the fastest, most trusted insurers are doing differently.",
    keywords: [
      "AI for insurance UK",
      "AI claims processing",
      "insurance fraud detection AI",
      "AI underwriting",
      "insurance customer experience AI",
      "insurtech AI",
    ],
  },
  "/blog/best-lawyers-least-time-practising-law": {
    title: "Your lawyers trained for the work that matters. Why is admin getting in the way?",
    description:
      "Lawyers lose hundreds of hours a year to admin that has nothing to do with legal work. Here's what changes when firms stop accepting that as normal.",
    keywords: [
      "legal workflow automation UK",
      "AI document review law firms",
      "law firm admin automation",
      "AI for solicitors UK",
      "legal AI tools UK",
      "AI contract review",
    ],
  },
  "/blog/elderly-care-crisis-intervention": {
    title: "The elderly care gap is here. The system isn't ready.",
    description:
      "Falls cost the NHS £2.3 billion a year. Dementia takes 3.5 years to diagnose. The UK's elderly care system is under structural pressure. Here's where earlier intervention changes outcomes.",
    keywords: [
      "remote patient monitoring UK",
      "patient engagement between appointments",
      "personalised patient care",
      "patient pathway management",
      "elderly care healthcare UK",
      "independent living elderly",
    ],
  },
  "/blog/chronic-disease-continuous-monitoring": {
    title: "The quarterly check-up cannot manage a condition that changes daily.",
    description:
      "70% of NHS spending goes on chronic disease. The care model is built on appointments every three to six months. Here's why that mismatch has consequences and what continuous monitoring changes.",
    keywords: [
      "chronic disease management UK",
      "remote patient monitoring UK",
      "medication adherence",
      "patient engagement between appointments",
      "personalised patient care",
      "precision medicine UK",
    ],
  },
  "/blog/medication-adherence-non-adherence-nhs": {
    title: "The medication isn't working if nobody is taking it.",
    description:
      "Medication non-adherence costs the NHS over £930 million a year and half of all chronic disease patients are not taking their prescriptions as directed. Here's what the evidence shows about why, and what actually changes behaviour.",
    keywords: [
      "medication adherence",
      "patient engagement between appointments",
      "personalised patient care",
      "remote patient monitoring UK",
      "chronic disease management UK",
      "medication non-adherence NHS",
    ],
  },
  "/blog/clinical-data-entry-errors-document-automation": {
    title: "21% of patients who check their records find an error in them.",
    description:
      "Manual clinical data entry has a 3 to 5% error rate under typical working conditions. 21% of patients who review their records find mistakes and 42% of those errors are described as serious. Here is what document automation actually changes.",
    keywords: [
      "clinical admin reduction",
      "NHS admin burden",
      "clinical workflow automation",
      "healthcare operational efficiency",
      "private clinic management UK",
      "clinical document processing",
    ],
  },
  "/blog/referral-management-patient-pathway": {
    title: "One in seven NHS referrals is lost, delayed, or rejected.",
    description:
      "One in seven patients referred by their GP ends up stuck waiting for care that never arrived. 71% only find out after chasing it themselves. Here is what is actually happening in referral management and where the gaps are costing patients and practices.",
    keywords: [
      "patient pathway management",
      "clinical workflow automation",
      "new patient conversion healthcare",
      "private clinic management UK",
      "referral management healthcare",
      "clinical admin reduction",
    ],
  },
  "/blog/unanswered-patient-messages": {
    title: "Patients switch providers over communication and 69% of them say so.",
    description:
      "69% of patients will switch providers over poor communication, up from 51% in 2023. Practices miss between 23% and 42% of incoming calls and 85% of those callers never try again. Here is where the communication gap is actually costing practices.",
    keywords: [
      "patient engagement between appointments",
      "private practice patient journey",
      "patient acquisition private practice",
      "healthcare patient experience",
      "out of hours patient communication",
      "private clinic patient communication",
    ],
  },
  "/blog/insurance-claims-administration-private-practice": {
    title: "73% of providers report more denials. Only 14% are automating.",
    description:
      "73% of healthcare providers report rising claim denial rates. 82% say denial reduction is their top priority. Only 14% have automated their claims process. Here is where the revenue is leaking and what fixing it actually looks like.",
    keywords: [
      "clinical admin reduction",
      "healthcare operational efficiency",
      "private clinic management UK",
      "clinical workflow automation",
      "insurance claims management healthcare",
      "revenue cycle management UK",
    ],
  },
  "/blog/first-90-days-automation-sequence": {
    title: "The first 90 days of automation done right.",
    description:
      "Most people know they should be doing something with automation and have no idea what the first move is. Here is the exact sequence, in order, that gets a first implementation working and sets up everything that follows.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "process automation UK",
      "business efficiency UK",
      "operational efficiency UK",
      "automation implementation UK",
    ],
  },
  "/blog/business-automation-capability-line": {
    title: "The automation capability line is not where you think it is.",
    description:
      "Most businesses either overestimate or underestimate what automation can do. Here is a plain map of where the capability line currently sits, what delivers real returns, and why the boring applications almost always win.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "process automation UK",
      "business efficiency UK",
      "operational efficiency UK",
      "AI implementation UK",
    ],
  },
  "/blog/cqc-compliance-continuous-private-practice": {
    title: "The CQC has changed its model. Compliance is now continuous.",
    description:
      "The CQC's Single Assessment Framework requires continuous evidence of compliance rather than inspection readiness. 9,000 assessments are planned by September 2026. Here is what that means for private practices and where the administrative burden is actually coming from.",
    keywords: [
      "clinical admin reduction",
      "NHS admin burden",
      "clinical workflow automation",
      "healthcare operational efficiency",
      "private clinic management UK",
      "CQC compliance private practice",
    ],
  },
  "/case-studies/single-handed-practice-real-cost": {
    title: "What the smallest practice we worked with taught us.",
    description:
      "One doctor, one part-time assistant, and a practice held together entirely by one person's memory and willingness to never fully switch off. Here is what we built and what changed when she finally took a week off for the first time in two years.",
    keywords: [
      "private practice management UK",
      "healthcare operational efficiency",
      "single-handed practice management",
      "out of hours patient enquiries",
      "private clinic management UK",
      "clinical admin reduction",
    ],
  },
  "/case-studies/22-to-58-enquiry-conversion": {
    title: "Two clinics were losing patients to nothing but the clock",
    description:
      "Two thirds of enquiries arrived after 6pm. The average response time was 14 hours and 40 minutes. Here is what happened when we reduced that to 90 seconds.",
    keywords: [
      "private practice patient journey",
      "new patient conversion healthcare",
      "patient acquisition private practice",
      "out of hours patient enquiries",
      "healthcare patient experience",
      "private clinic management UK",
    ],
  },
  "/case-studies/practice-manager-would-not-let-go": {
    title: "The practice manager who would not let go",
    description:
      "We were brought in to make a private physio practice more efficient. The practice manager showed us we were about to build the wrong thing. Here is what she taught us and what we built instead.",
    keywords: [
      "private practice management UK",
      "healthcare operational efficiency",
      "clinical admin reduction",
      "out of hours patient enquiries",
      "private clinic management UK",
      "healthcare reception management",
    ],
  },
  "/blog/founder-biggest-blocker-automation": {
    title: "The Founder Is Usually the Biggest Blocker in the Business",
    description:
      "The businesses where automation would free up the most time are frequently the ones where it is hardest to implement. Not because of the technology or the budget. Because of the founder. Here is why and what we do about it.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "operational efficiency UK",
      "business efficiency UK",
      "automation consultancy UK",
      "digital transformation UK",
    ],
  },
  "/blog/practice-inbox-triage-system": {
    title: "Your practice inbox has no triage and your patients know it.",
    description:
      "Practice administrators spend 28% of their working week managing an inbox with no systematic triage behind it. A new patient enquiry is 21 times less likely to convert if the response takes more than five minutes. Here is what changes when that system finally exists.",
    keywords: [
      "private practice management UK",
      "healthcare operational efficiency",
      "clinical admin reduction",
      "patient engagement between appointments",
      "private clinic management UK",
      "practice inbox management healthcare",
    ],
  },
  "/blog/small-business-advantage-automation-wave": {
    title: "The first technology wave that does not favour scale.",
    description:
      "Every previous enterprise technology wave required capital and specialist staff that only large organisations could afford. This one does not. Here is why small businesses have a structural advantage in this shift and what that means in practice.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "business efficiency UK",
      "operational efficiency UK",
      "digital transformation UK",
      "small business automation UK",
    ],
  },
  "/blog/why-projects-fail-four-questions": {
    title: "80% of AI projects fail and these four questions show why.",
    description:
      "RAND Corporation analysed 2,400 enterprise AI initiatives and found 80.3% failed to deliver business value. The reasons are almost never technical. Here are the four questions that predict whether a project will survive.",
    keywords: [
      "business automation UK",
      "workflow automation UK",
      "digital transformation UK",
      "business efficiency UK",
      "operational efficiency UK",
      "AI project implementation UK",
    ],
  },
  "/blog/private-practice-capacity-utilisation-uk": {
    title: "The average UK clinic runs at 72% capacity and here is the gap.",
    description:
      "The average UK private practice diary utilisation rate is 72.3% according to a 2026 survey of 700 clinic owners. Here is what that gap actually costs, where it comes from, and what closing it looks like without adding staff or clinical sessions.",
    keywords: [
      "private practice management UK",
      "medical practice operations",
      "private clinic management UK",
      "healthcare practice growth",
      "clinical capacity management",
      "practice scheduling optimisation",
    ],
  },
  "/blog/ambient-scribing-clinical-documentation-nhs": {
    title: "Generative AI in the clinic is no longer an experiment.",
    description:
      "The NHS has published its ambient scribing results. 23.5% more time with patients. 47 minutes saved per A&E clinician per shift. Here is what generative AI in clinical settings actually looks like now and which applications are ready.",
    keywords: [
      "clinical admin reduction",
      "NHS admin burden",
      "clinical workflow automation",
      "healthcare operational efficiency",
      "ambient scribe NHS",
      "generative AI clinical practice UK",
    ],
  },
  "/blog/wearable-health-data-remote-monitoring": {
    title: "Your patients are generating clinical data nobody is reading.",
    description:
      "Millions of people in the UK now wear continuous health monitors. The clinical data they generate is largely disconnected from the healthcare system. Here is what connecting it changes for patients, practices, and outcomes.",
    keywords: [
      "remote patient monitoring UK",
      "patient engagement between appointments",
      "personalised patient care",
      "precision medicine UK",
      "wearable health data clinical",
      "continuous monitoring healthcare UK",
    ],
  },
  "/blog/nhs-waiting-list-patient-pathway-management": {
    title: "The waiting list has 7.3 million names behind the numbers.",
    description:
      "There are 7.3 million people on NHS waiting lists in England and 548,000 were removed in the past year without receiving treatment. Here is what is actually happening inside the waiting list and where the real inefficiencies are costing patients time they cannot afford.",
    keywords: [
      "patient pathway management",
      "healthcare operational efficiency",
      "private practice patient journey",
      "new patient conversion healthcare",
      "NHS waiting list management",
      "private healthcare UK",
    ],
  },
  "/blog/mental-health-waiting-times-digital-support": {
    title: "One in four will need help. One in eight will get it.",
    description:
      "There are 1.7 million people on mental health waiting lists in England. Mental health services received 5.2 million referrals in 2024. Here is where digital support is already scaling access in a system that cannot grow fast enough.",
    keywords: [
      "mental health waiting times UK",
      "digital mental health support",
      "patient engagement between appointments",
      "personalised patient care",
      "clinical decision support UK",
      "healthcare operational efficiency",
    ],
  },
  "/blog/prevention-population-health-nhs": {
    title: "The NHS treats first and here is the cost of that choice",
    description:
      "Prevention returns a median of £14 for every £1 invested yet only 5% of NHS funding goes to preventive care. Here is what population health management and proactive outreach are already delivering.",
    keywords: [
      "preventive care NHS",
      "population health management UK",
      "patient pathway management",
      "personalised patient care",
      "healthcare operational efficiency",
      "patient engagement between appointments",
    ],
  },
  "/blog/health-inequality-access-gap": {
    title: "Your postcode still determines your health and it's time that changes",
    description:
      "There is a 19-year gap in healthy life expectancy between the most and least deprived areas in England. Here is where technology is already closing the access gap and what that changes for the patients who have historically had the least.",
    keywords: [
      "health inequality UK",
      "preventive care NHS",
      "patient pathway management",
      "personalised patient care",
      "healthcare operational efficiency",
      "patient engagement between appointments",
    ],
  },
};


export function getRouteMeta(pathname: string): RouteMeta {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];
  // strip trailing slash
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  if (trimmed.startsWith("/roi-calculator/")) {
    const industry = INDUSTRIES.find((item) => item.id === trimmed.slice("/roi-calculator/".length));
    if (industry) return {
      title: `AI ROI calculator for ${industry.name.toLowerCase()} | Astreon AI Group`,
      description: `Estimate recoverable revenue from missed enquiries and admin time in ${industry.name.toLowerCase()}. Explore AI front desk savings with editable assumptions.`,
    };
  }
  return ROUTE_META[trimmed] ?? DEFAULT_META;
}

// Routes that duplicate another page's content. The canonical points at the primary URL.
export const CANONICAL_OVERRIDES: Record<string, string> = {
  "/contact": "/contact-us",
  "/book": "/book-a-demo",
};

// Utility, confirmation and form-only routes that carry no indexable content.
export const NOINDEX_ROUTES = new Set<string>([
  "/booking-confirmed",
  "/clinic-genius-thank-you",
  "/thank-you",
  "/unsubscribe",
  "/careers/apply",
  "/pricing-request",
]);

// Only supported calculator topics are indexable parameterised routes.
export function isKnownRoute(pathname: string): boolean {
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  if (trimmed.startsWith("/roi-calculator/")) return INDUSTRIES.some((item) => item.id === trimmed.slice("/roi-calculator/".length));
  return Boolean(ROUTE_META[pathname] || ROUTE_META[trimmed] || NOINDEX_ROUTES.has(trimmed));
}

export function getCanonicalPath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  return CANONICAL_OVERRIDES[trimmed] ?? trimmed;
}
